// src/components/reports/VaccineProductionChart.js
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardHeader } from "../../themes/StandardHeader";

const VaccineProductionChart = ({ data, vaccineOptions, budgetYear }) => {
  const [selectedVaccine, setSelectedVaccine] = useState("");

  // Handle dropdown change
  const handleVaccineChange = (e) => {
    setSelectedVaccine(e.target.value);
  };

  // Prepare chart data
  const chartData = () => {
    const prevYear = parseInt(budgetYear || 2568) - 1;
    const yearShort = (budgetYear || 2568).toString().slice(-2);
    const prevYearShort = prevYear.toString().slice(-2);

    const months = [
      { key: "OctQty", label: `ต.ค. ${prevYearShort}` },
      { key: "NovQty", label: `พ.ย. ${prevYearShort}` },
      { key: "DecQty", label: `ธ.ค. ${prevYearShort}` },
      { key: "JanQty", label: `ม.ค. ${yearShort}` },
      { key: "FebQty", label: `ก.พ. ${yearShort}` },
      { key: "MarQty", label: `มี.ค. ${yearShort}` },
      { key: "AprQty", label: `เม.ย. ${yearShort}` },
      { key: "MayQty", label: `พ.ค. ${yearShort}` },
      { key: "JunQty", label: `มิ.ย. ${yearShort}` },
      { key: "JulQty", label: `ก.ค. ${yearShort}` },
      { key: "AugQty", label: `ส.ค. ${yearShort}` },
      { key: "SepQty", label: `ก.ย. ${yearShort}` },
    ];

    if (!selectedVaccine) {
      // Aggregate all vaccines
      return months.map((month) => {
        const totalQty = data.reduce(
          (sum, item) => sum + (item[month.key] || 0),
          0
        );
        return {
          month: month.label,
          quantity: totalQty,
          vaccine: "ทุกวัคซีน",
        };
      });
    } else {
      // Filter by selected vaccine
      const vaccineData = data.find(
        (item) => item.VaccineID === selectedVaccine
      );
      if (!vaccineData) return [];

      return months.map((month) => ({
        month: month.label,
        quantity: vaccineData[month.key] || 0,
        vaccine: vaccineData.VaccineName || "ไม่ระบุ",
      }));
    }
  };

  // Bar color
  const barColor = "#16A085";

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>เดือน:</strong> {label}
          </p>
          <p>
            <strong>วัคซีน:</strong> {data.vaccine}
          </p>
          <p>
            <strong>ปริมาณ:</strong> {Number(data.quantity).toLocaleString("th-TH")}
          </p>
        </Box>
      );
    }
    return null;
  };

  // Guard against undefined vaccineOptions
  if (!vaccineOptions || !Array.isArray(vaccineOptions)) {
    return (
      <Box
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        p={5}
        mt={6}
        bg="white"
      >
        <StandardHeader
          title="กราฟปริมาณความต้องการวัคซีน"
          bgColor="iGreen.500"
          icon={faChartBar}
        />
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่สามารถโหลดตัวเลือกวัคซีนได้
        </Text>
      </Box>
    );
  }

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <StandardHeader
        title="กราฟปริมาณความต้องการวัคซีน"
        bgColor="iGreen.500"
        icon={faChartBar}
      />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกวัคซีน
        </FormLabel>
        <Select
          value={selectedVaccine}
          onChange={handleVaccineChange}
          placeholder="ทุกวัคซีน"
          colorScheme="iBlue"
        >
          {vaccineOptions.map((vaccine) => (
            <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
              {vaccine.VaccineNameTH}
            </option>
          ))}
        </Select>
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={100}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "ปริมาณ",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="quantity"
              name="ปริมาณ"
              fill={barColor}
              barSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineProductionChart;